import { useState } from "react";
import { ArrowRight, ArrowLeft, Check, GraduationCap, BookOpen, Sparkles, Clock } from "lucide-react";
import { api, formatINR, formatApiErr } from "@/lib/api";
import { COUNTRIES } from "@/data/site";
import { toast } from "sonner";

const COURSES = [
    {
        slug: "vedic-basic",
        name: "Vedic Astrology — Basic",
        priceInr: 35000,
        duration: "3 months",
        tag: "Foundation",
        icon: BookOpen,
        blurb: "From rashis & nakshatras to planetary periods — the complete bedrock of Jyotish, taught with chart-reading practice every week.",
        modules: ["The 9 grahas & their roles", "12 rashis & 12 bhavas", "27 nakshatras", "Karakatva basics", "Dasha system (Vimshottari)", "Live chart-reading workshops"],
    },
    {
        slug: "vedic-advanced",
        name: "Vedic Astrology — Advanced",
        priceInr: 55000,
        duration: "6 months",
        tag: "Practitioner",
        icon: GraduationCap,
        blurb: "From foundation to professional consultation. Divisional charts, transit analysis, predictive techniques, remedies, and live case studies.",
        modules: ["All 16 divisional charts (D1–D60)", "Advanced transit techniques", "Predictive yoga systems", "Mahadasha + Antardasha + Pratyantar", "Marriage, career & wealth prediction", "Remedies framework", "20+ real client case studies"],
    },
    {
        slug: "bnn",
        name: "Bhrigu Nandi Nadi (BNN)",
        priceInr: 40000,
        duration: "3 months",
        tag: "Signature",
        icon: Sparkles,
        blurb: "The rare BNN system — Dr. Tannu's signature specialization. Planetary couples, karakatva pairs, and precise event-timing methodology.",
        modules: ["BNN philosophy & history", "Karaka–Karakamsa pairing", "Planetary couples in detail", "Event-timing techniques", "BNN vs traditional Jyotish", "10+ BNN case studies"],
    },
];

export default function Courses() {
    const [selected, setSelected] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", country: "India", experience: "Beginner", message: "" });
    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!selected) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/payments/course-checkout", {
                course_slug: selected.slug,
                ...form,
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (err) {
            toast.error(formatApiErr(err.response?.data?.detail) || "Could not start checkout");
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="courses-page" className="pt-32 pb-20">
            <div className="container-vv">
                <div className="max-w-3xl">
                    <div className="label-eyebrow flex items-center gap-2"><GraduationCap className="h-3.5 w-3.5" /> Learn With Dr. Tannu</div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream mt-4 text-balance">
                        Study <span className="italic text-gold">Jyotish</span> with a Gold-Medalist Acharya.
                    </h1>
                    <p className="mt-6 text-cream-muted leading-relaxed text-lg">
                        Live online courses — limited cohort sizes, hand-graded assignments, and lifetime access to recorded sessions. Designed for serious students, working professionals, and aspiring consultants.
                    </p>
                </div>

                <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {COURSES.map((c) => {
                        const Icon = c.icon;
                        return (
                            <div key={c.slug} className="card-luxe flex flex-col" data-testid={`course-card-${c.slug}`}>
                                <div className="flex items-start justify-between mb-6">
                                    <div className="h-12 w-12 rounded-xl border border-gold/30 grid place-items-center bg-ink-deep/60">
                                        <Icon className="h-5 w-5 text-gold" />
                                    </div>
                                    <span className="text-[10px] tracking-widest uppercase text-gold border border-gold/40 px-2 py-1 rounded-full">{c.tag}</span>
                                </div>
                                <h3 className="font-serif text-2xl text-cream mb-2">{c.name}</h3>
                                <div className="flex items-center gap-2 text-xs text-cream-muted mb-4">
                                    <Clock className="h-3.5 w-3.5" /> {c.duration}
                                </div>
                                <p className="text-sm text-cream-muted leading-relaxed mb-6">{c.blurb}</p>
                                <ul className="space-y-2 mb-6 flex-1">
                                    {c.modules.map((m) => (
                                        <li key={m} className="flex items-start gap-2 text-xs text-cream">
                                            <Check className="h-3.5 w-3.5 text-gold mt-0.5 flex-shrink-0" />
                                            <span>{m}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="pt-5 border-t border-gold/15 flex items-center justify-between">
                                    <div className="font-serif text-2xl text-gold">{formatINR(c.priceInr)}</div>
                                    <button onClick={() => setSelected(c)} className="text-sm text-gold flex items-center gap-1 hover:gap-2 transition-all" data-testid={`enroll-${c.slug}`}>
                                        Enroll <ArrowRight className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {selected && (
                    <form onSubmit={submit} className="card-luxe mt-14 max-w-3xl mx-auto" data-testid="course-enroll-form">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <div className="label-eyebrow">Enrollment</div>
                                <h2 className="font-serif text-2xl text-cream mt-1">{selected.name}</h2>
                                <div className="text-xs text-cream-muted mt-1">{selected.duration} · {formatINR(selected.priceInr)}</div>
                            </div>
                            <button type="button" onClick={() => setSelected(null)} className="text-xs text-cream-muted hover:text-gold flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> Back</button>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs tracking-widest uppercase text-cream-muted">Full name *</label>
                                <input className="input-luxe mt-2" required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="course-name" />
                            </div>
                            <div>
                                <label className="text-xs tracking-widest uppercase text-cream-muted">Email *</label>
                                <input type="email" className="input-luxe mt-2" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="course-email" />
                            </div>
                            <div>
                                <label className="text-xs tracking-widest uppercase text-cream-muted">Phone *</label>
                                <input className="input-luxe mt-2" required value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="course-phone" />
                            </div>
                            <div>
                                <label className="text-xs tracking-widest uppercase text-cream-muted">Country *</label>
                                <select className="input-luxe mt-2" value={form.country} onChange={(e) => update("country", e.target.value)} data-testid="course-country">
                                    {COUNTRIES.map((c) => <option key={c} value={c} className="bg-ink">{c}</option>)}
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-xs tracking-widest uppercase text-cream-muted">Astrology background</label>
                                <select className="input-luxe mt-2" value={form.experience} onChange={(e) => update("experience", e.target.value)} data-testid="course-experience">
                                    <option className="bg-ink">Beginner</option>
                                    <option className="bg-ink">Some self-study</option>
                                    <option className="bg-ink">Intermediate</option>
                                    <option className="bg-ink">Practising astrologer</option>
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-xs tracking-widest uppercase text-cream-muted">Anything you'd like Dr. Tannu to know?</label>
                                <textarea rows={3} className="input-luxe mt-2" value={form.message} onChange={(e) => update("message", e.target.value)} data-testid="course-message" />
                            </div>
                        </div>

                        <div className="mt-8 pt-6 border-t border-gold/15 flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="text-cream-muted text-xs">Secure Stripe checkout · Email + WhatsApp confirmation</div>
                            <button type="submit" disabled={submitting} className="btn-gold disabled:opacity-50" data-testid="course-pay-btn">
                                {submitting ? "Redirecting..." : <>Pay {formatINR(selected.priceInr)} & Enroll <ArrowRight className="h-4 w-4" /></>}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
